import type { FC } from 'react';
import { useDispatch } from 'react-redux';
import APITable from '@/components/UI/Table/Reusable/APITable';
import Header from '@/components/UI/Header';
import Button from '@/components/UI/Button';
import type { TanStackColumnDef } from '@/components/UI/Table/TableComponent.model';
import type { UseTableProps, TableFilter } from '@/components/UI/Table/Reusable/APITable/APITable.model';

import type { FilterOption } from '@/components/UI/Table/TableComponent.model';
import { _notNil, _isNil, _isEmptyString } from '@/littledash';
import type { TeamGlossaryV1 } from '@/model/Glosary.model';
import { ApiService } from '@/support/ApiService.ts';
import { errorToast, successToast } from '@/helpers';
import { ExceptionHandler } from '@/utils/ExceptionHandler';
import InVivoError from '@/model/InVivoError';

import { FilterTypes as filterType } from '@/constants/FilterTypes';
import { RouteService } from '@/support/RouteService';
import { DEFAULT_NUMERIC_SCALE_MIN, DEFAULT_NUMERIC_SCALE_MAX } from '@/utils/observationTypeValidation';

function generateColumns(): Array<TanStackColumnDef<TeamGlossaryV1>> {
  return [
    {
      id: 'title',
      accessorKey: 'title',
      header: 'Observation type',
      size: 300,
      isVisible: true,
      enableSorting: false,
    },
    {
      id: 'label',
      accessorKey: 'label',
      header: 'Label',
      size: 120,
      isVisible: true,
      enableSorting: false,
      cell: ({ row }: { row: { original: TeamGlossaryV1 } }) =>
        _notNil(row.original.label) ? row.original.label : '-',
    },
    {
      id: 'scoring_scale',
      accessorKey: 'type',
      header: 'Scoring scale',
      size: 150,
      isVisible: true,
      enableSorting: false,
      cell: ({ row }: { row: { original: TeamGlossaryV1 } }) => {
        const { type, options } = row.original;

        if (type === 'numeric' && _notNil(options)) {
          const min = options.min ?? DEFAULT_NUMERIC_SCALE_MIN;
          const max = options.max ?? DEFAULT_NUMERIC_SCALE_MAX;
          return `Numeric (${min}-${max})`;
        }

        if (type === 'severity') {
          return 'Severity';
        }

        return '-';
      },
    },
    {
      id: 'grading_information',
      accessorKey: 'description',
      header: 'Grading information',
      size: 200,
      isVisible: true,
      enableSorting: false,
      cell: ({ row }: { row: { original: TeamGlossaryV1 } }) => {
        const description = row.original.description?.trim();
        return !_isEmptyString(description) ? description : '-';
      },
    },
    {
      id: 'status',
      accessorKey: 'archived_at',
      header: 'Status',
      size: 100,
      isVisible: true,
      enableSorting: false,
      cell: ({ row }: { row: { original: TeamGlossaryV1 } }) =>
        _isNil(row.original.archived_at) ? 'Active' : 'Archived',
    },
  ];
}

function generateFilterOptions(): Array<FilterOption> {
  return [
    {
      value: 'status',
      name: 'Status',
      operations: [
        {
          name: filterType.eq,
          value: filterType.eq,
          options: [
            { value: 'active', name: 'Active' },
            { value: 'archived', name: 'Archived' },
          ],
        },
      ],
    },
  ];
}

function createDefaultActiveFilter(): TableFilter {
  return {
    category: {
      name: 'Status',
      value: 'status',
      index: 0,
    },
    operation: {
      name: filterType.eq,
      index: 0,
    },
    option: {
      name: 'Active',
      value: 'active',
    },
  };
}

const ObservationGlossaries: FC = () => {
  const dispatch = useDispatch();
  const columns = generateColumns();
  const filterOptions = generateFilterOptions();
  const defaultFilters = [createDefaultActiveFilter()];

  function handleBulkEdit(observationTypes: Array<TeamGlossaryV1>, refreshTable: () => void): void {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'BULK_EDIT_OBSERVATION_TYPES',
      props: {
        observationTypes,
        onSave: async () => {
          dispatch({ type: 'CLOSE_MODAL' });
          await refreshTable();
        },
        onCancel: () => {
          dispatch({ type: 'CLOSE_MODAL' });
        },
      },
    });
  }

  async function handleArchive(observationTypes: Array<TeamGlossaryV1>, refreshTable: () => void): Promise<void> {
    const teamGlossaryApiIds = observationTypes.map((observationType) => observationType.api_id);

    try {
      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/archive',
        path: undefined,
        body: { team_glossary_api_ids: teamGlossaryApiIds },
        options: { onError: { toast: false, capture: false, throw: true, slug: 'observation-glossary-archive' } },
      });

      successToast(
        `Successfully archived ${observationTypes.length} observation type${observationTypes.length !== 1 ? 's' : ''}`
      );
      await refreshTable();
    } catch (error) {
      errorToast('Could not archive observation types');
      ExceptionHandler.captureException(
        new InVivoError('Could not archive observation types', {
          slug: 'observation-glossary-archive',
          cause: error,
        })
      );
    }
  }

  async function handleUnarchive(observationTypes: Array<TeamGlossaryV1>, refreshTable: () => void): Promise<void> {
    const teamGlossaryApiIds = observationTypes.map((observationType) => observationType.api_id);

    try {
      await ApiService.call({
        endpoint: 'POST /api/v1/team-glossary/unarchive',
        path: undefined,
        body: { team_glossary_api_ids: teamGlossaryApiIds },
        options: { onError: { toast: false, capture: false, throw: true, slug: 'observation-glossary-unarchive' } },
      });

      successToast(
        `Successfully unarchived ${observationTypes.length} observation type${observationTypes.length !== 1 ? 's' : ''}`
      );
      await refreshTable();
    } catch (error) {
      errorToast('Could not unarchive observation types');
      ExceptionHandler.captureException(
        new InVivoError('Could not unarchive observation types', {
          slug: 'observation-glossary-unarchive',
          cause: error,
        })
      );
    }
  }

  function handleBulkAdd(refreshTable: () => void): void {
    dispatch({
      type: 'OPEN_MODAL',
      modal: 'BULK_ADD_OBSERVATION_TYPES',
      props: {
        onSave: async () => {
          dispatch({ type: 'CLOSE_MODAL' });
          await refreshTable();
        },
        onCancel: () => {
          dispatch({ type: 'CLOSE_MODAL' });
        },
      },
    });
  }

  function bulkActions({
    useTableProps: {
      selectedFlatRows,
      apiTable: { refetchTableData },
    },
  }: UseTableProps<TeamGlossaryV1>): Array<{
    name: string;
    key: string;
    action: () => void;
    disabled?: boolean;
    tooltip?: string;
    className?: string;
  }> {
    const selectedObservationTypes = selectedFlatRows.map(({ original }) => original);

    const archivedCount = selectedObservationTypes.filter((item) => _notNil(item.archived_at)).length;
    const unarchivedCount = selectedObservationTypes.length - archivedCount;
    const hasMixedSelection = archivedCount > 0 && unarchivedCount > 0;
    const allArchived = archivedCount === selectedObservationTypes.length;

    const archiveAction = allArchived ? 'Unarchive' : 'Archive';
    const archiveHandler = allArchived ? handleUnarchive : handleArchive;

    return [
      {
        name: 'Edit',
        key: 'edit_observation_types',
        action: () => {
          handleBulkEdit(selectedObservationTypes, refetchTableData);
        },
      },
      {
        name: archiveAction,
        key: 'archive_observation_types',
        action: () => {
          if (!hasMixedSelection) {
            archiveHandler(selectedObservationTypes, refetchTableData);
          }
        },
        disabled: hasMixedSelection,
        tooltip: hasMixedSelection
          ? 'Cannot archive and unarchive items in the same operation. Please select only archived or only unarchived items.'
          : undefined,
        className: 'red',
      },
    ];
  }

  const AsideComponent = ({ useTableProps }: any) => {
    const {
      apiTable: { fetchTableData },
    } = useTableProps;

    return (
      <Button onClick={() => handleBulkAdd(fetchTableData)} testId="create-observation-type-button">
        Add new
      </Button>
    );
  };

  return (
    <div>
      <Header mainHeaderText="Observations" className="pb2" />
      <APITable
        columns={columns}
        filterOptions={filterOptions}
        apiInfo={{
          type: 'internalApi',
          route: RouteService.api({
            endpoint: 'GET /api/v1/team-glossary',
            path: undefined,
            query: {
              group: 'observations',
              sort: 'title',
            },
          }).url,
        }}
        reduxTableName="observationGlossaries"
        queryParams={{
          group: 'observations',
          sort: 'title',
        }}
        defaultSortBy={{ id: 'title', desc: false }}
        defaultFilters={defaultFilters}
        searchPlaceholderText="Search observation types..."
        pageSizes={[10, 25, 50, 100]}
        defaultPageSize={25}
        testIdPrefix="observations-v2"
        loadingText="Loading observation types..."
        noDataMessage="No observation types found"
        bulkActions={bulkActions}
        AsideComponent={AsideComponent}
      />
    </div>
  );
};

export default ObservationGlossaries;
